import { useCallback, useEffect, useState } from "react"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { LoadState } from "@/components/ui/LoadState"

type EndingSoonLowProgress = {
  seedbedId: string
  seedbedName: string
  endDate: string | null
  averageProgressPercent: number
}

type IncompleteStructure = {
  seedbedId: string
  seedbedName: string
  startDate: string | null
  missingGroups: boolean
  missingCourseAssignment: boolean
}

type ZeroOrNearZeroEnrollment = {
  seedbedId: string
  seedbedName: string
  startDate: string | null
  enrollmentCount: number
}

type SeedbedHealthStats = {
  endingSoonWithLowProgress: EndingSoonLowProgress[]
  incompleteStructure: IncompleteStructure[]
  zeroOrNearZeroEnrollment: ZeroOrNearZeroEnrollment[]
  undatedSeedbedsCount: number
}

export default function SeedbedHealthStatsPage() {
  const [data, setData] = useState<SeedbedHealthStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get("/academy/seedbeds/admin/health-stats")
      setData(res.data.data ?? res.data)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Semilleros", "Estadísticas"]}
      title="Salud de semilleros"
      subtitle="Riesgos operativos — no matrícula total"
      tabs={[
        { label: "Semilleros", to: "/programs/seedbeds" },
        { label: "Estadísticas", to: "/programs/seedbeds/stats", active: true },
      ]}
      metrics={[
        { label: "Por terminar con progreso bajo", value: String(data?.endingSoonWithLowProgress.length ?? "—"), tone: "warning" },
        { label: "Sin estructura completa", value: String(data?.incompleteStructure.length ?? "—"), tone: "warning" },
        { label: "Matrícula nula/casi nula", value: String(data?.zeroOrNearZeroEnrollment.length ?? "—"), tone: "danger" },
        { label: "Sin calendario definido", value: String(data?.undatedSeedbedsCount ?? "—") },
      ]}
    >
      {loading && <LoadState />}
      {error && <p role="alert" aria-live="assertive" className="text-error-600 dark:text-error-400">{error}</p>}
      {!loading && !error && data && (
        <div className="space-y-6">
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Por terminar con progreso bajo
            </h3>
            {data.endingSoonWithLowProgress.length === 0 ? (
              <p className="text-sm text-gray-500">Ninguno en riesgo de no completar a tiempo.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.endingSoonWithLowProgress.map((s) => (
                  <li key={s.seedbedId} className="flex justify-between">
                    <span>{s.seedbedName}</span>
                    <span className="text-gray-500">{s.averageProgressPercent}% de progreso promedio</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Sin estructura completa antes de iniciar
            </h3>
            {data.incompleteStructure.length === 0 ? (
              <p className="text-sm text-gray-500">Todos los semilleros iniciados tienen grupo y curso asignado.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.incompleteStructure.map((s) => (
                  <li key={s.seedbedId} className="flex justify-between">
                    <span>{s.seedbedName}</span>
                    <span className="text-gray-500">
                      {[s.missingGroups && "sin grupo", s.missingCourseAssignment && "sin curso"].filter(Boolean).join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Matrícula nula o casi nula (iniciados hace más de 14 días)
            </h3>
            {data.zeroOrNearZeroEnrollment.length === 0 ? (
              <p className="text-sm text-gray-500">Ningún lanzamiento parece haber fallado.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.zeroOrNearZeroEnrollment.map((s) => (
                  <li key={s.seedbedId} className="flex justify-between">
                    <span>{s.seedbedName}</span>
                    <span className="text-gray-500">{s.enrollmentCount} matriculado(s)</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </ModuleLayout>
  )
}
